// How a live event ANNOUNCES itself on the map — the one definition, shared by
// the renderer (objects.ts), the admin editor and the editor's preview.
//
// Why this is configuration and not code. The arrival animation used to be two
// hand-written self-animating sprites: `makeFreshPulse` (a red double ring under
// every fresh event) and `makeLaunchPulse` (an amber rocket, for `launch` only).
// The second one is the tell — a hard-coded per-kind exception means "make new
// events louder" was a code change, and "make launches louder than impacts" was
// not expressible at all. Both are now the same thing: a STYLE, with a default
// that applies to every kind and per-kind overrides that replace it wholesale.
//
// Wholesale, deliberately, not a partial patch. An override is created by
// copying the default and editing it, so "customised" means exactly what it
// says — a later change to the default does not reach in and move a kind
// somebody tuned by hand. It also keeps the editor honest: every field shows
// the value that will actually render, never an inherited ghost.
//
// TWO CLOCKS, as everywhere else in this codebase (see when.ts):
//   the arrival animation (ring, size pulse) runs off the LAST REPORT — it asks
//   "is this still developing", and a six-hour-old strike a channel just
//   corroborated SHOULD announce itself again;
//   the colour ramp runs off the OCCURRENCE — it asks "how old is this".
// Mixing them is the bug that made every merge repaint an old event as brand
// new. The renderer bakes both separately and this module never conflates them.

import { applyGlyphOverrides, LIVE_KINDS, SHIPPED_GLYPHS } from './livekinds';

/** One stop on a style's own colour ramp. `atSeconds` is age since OCCURRENCE;
 *  the stop holds until the next one begins. An empty `stages` list means the
 *  global age ramp (AGE_COLORS × the admin's colour-change hours) — which is
 *  what every style ships with, so the global knob keeps working until someone
 *  deliberately takes a kind off it. */
export interface MarkerStage {
  atSeconds: number;
  color: string;
  /** Multiplied into the age fade, so "fade at 20 minutes" is a stop with a
   *  low opacity rather than a fourth timing knob. */
  opacity: number;
}

export interface EventMarkerStyle {
  // --- the expanding ring: "this just landed on the map" ---
  ring: boolean;
  /** '' = follow the marker's own colour (the stage/age ramp, or the operator's
   *  faction/class ring rule when one is set). A hex pins it regardless of age. */
  ringColor: string;
  /** Outer radius in screen pixels at the far end of one expansion. */
  ringMaxPx: number;
  ringWidthPx: number;
  /** One expansion, ms. Smaller = faster. */
  ringPeriodMs: number;
  /** How many rings are in flight at once, evenly spaced through the cycle. */
  ringCount: number;
  ringOpacity: number;
  /** How long the ring runs after the last report. 0 = the global fresh window
   *  (admin's "flashing time"), so one knob still moves every kind at once. */
  ringSeconds: number;

  // --- the soft halo under the marker while it is new ---
  halo: boolean;
  haloColor: string;   // '' = follow, as ringColor
  haloPx: number;
  haloOpacity: number;

  // --- the marker body ---
  dot: boolean;
  /** Dot radius in screen pixels at z12 (it scales down toward z4). */
  dotPx: number;
  symbol: boolean;
  symbolScale: number;
  /** Draw the kind glyph as a solid silhouette in the marker's current colour
   *  instead of the natural colour emoji. This is what makes "the symbol is red
   *  at first, then orange, then grey" possible at all — a colour-emoji font
   *  cannot be recoloured, so a tinted glyph is a different sprite. */
  symbolTint: boolean;

  // --- the size pulse while it is new ---
  pulse: boolean;
  /** Peak growth, percent. 0 = none, 100 = double size at the top of the beat. */
  pulsePct: number;
  pulsePeriodMs: number;
  /** 0 = the global fresh window, as ringSeconds. */
  pulseSeconds: number;

  /** What the marker settles to once it is no longer new — 1 keeps its size,
   *  0.6 shrinks it to background texture. */
  settleScale: number;

  stages: MarkerStage[];
}

export interface EventMarkerConfig {
  default: EventMarkerStyle;
  /** Full styles, keyed by live-event kind. A kind with no entry uses `default`. */
  kinds: Record<string, EventMarkerStyle>;
  /** The symbol a kind wears, keyed by kind — overrides ONLY; a kind with no
   *  entry keeps its shipped glyph (`SHIPPED_GLYPHS`). Reaches the map, the
   *  feed, the ticker and every kind filter, because they all read
   *  `LIVE_KINDS[kind].glyph` and `setEventMarkerConfig` rewrites that. */
  glyphs: Record<string, string>;
}

// Louder than what this replaced, on purpose — the ask was "new events need to
// be more noticeable", and every number here is now adjustable by an admin, so
// the defaults get to be opinionated rather than conservative. The old ring was
// 14 px of thin red at 1600 ms with no size pulse at all.
export const DEFAULT_MARKER_STYLE: EventMarkerStyle = {
  ring: true,
  ringColor: '',
  ringMaxPx: 26,
  ringWidthPx: 3,
  ringPeriodMs: 1500,
  ringCount: 2,
  ringOpacity: 0.85,
  ringSeconds: 0,
  halo: true,
  haloColor: '',
  haloPx: 16,
  haloOpacity: 0.22,
  dot: true,
  dotPx: 7,
  symbol: true,
  symbolScale: 1.1,
  symbolTint: false,
  pulse: true,
  pulsePct: 22,
  pulsePeriodMs: 900,
  pulseSeconds: 120,
  settleScale: 1,
  stages: [],
};

/** The per-kind overrides the app ships with. `launch` is here because it was
 *  a hard-coded exception before this module existed (an amber rocket sprite);
 *  turning it into a visible, editable row is the whole point — the special
 *  case is now something an operator can see, change or delete. */
export const DEFAULT_KIND_STYLES: Record<string, EventMarkerStyle> = {
  launch: {
    ...DEFAULT_MARKER_STYLE,
    ringColor: '#ff9628',
    ringMaxPx: 32,
    ringWidthPx: 3.5,
    ringPeriodMs: 1400,
    symbolScale: 1.3,
    pulsePct: 28,
    pulseSeconds: 180,
  },
};

export const DEFAULT_MARKER_CONFIG: EventMarkerConfig = {
  default: DEFAULT_MARKER_STYLE,
  kinds: DEFAULT_KIND_STYLES,
  glyphs: {},
};

/** A kind symbol as the operator typed it, or null for "no override". One to
 *  eight code points (a flag is two, a ZWJ family is seven) and none of the
 *  five HTML-significant characters: the glyph is interpolated into innerHTML
 *  at every read site, and an admin's typo must not become markup in every
 *  reader's feed. The backend mirrors both rules (settings/service.py). */
export const GLYPH_MAX_POINTS = 8;

export function normalizeGlyph(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const s = raw.trim();
  if (!s || [...s].length > GLYPH_MAX_POINTS || /[<>&"']/.test(s)) return null;
  return s;
}

// --- field spec ---------------------------------------------------------------
// The admin editor is GENERATED from this, so a new knob is one entry here plus
// one line in the renderer — never a hand-laid-out form that drifts from the
// interface above. The bounds are mirrored in backend/app/settings/service.py
// (`_MARKER_BOUNDS`); they must agree, and `tests/api/test_admin.py` pins the
// server half so a stricter server can't silently 422 a slider the UI offers.

export type FieldGroup = 'ring' | 'body' | 'pulse';

export interface MarkerField {
  key: keyof EventMarkerStyle;
  label: string;
  group: FieldGroup;
  kind: 'bool' | 'num' | 'color';
  min?: number;
  max?: number;
  step?: number;
  /** Colour fields only: offer an "follow the marker colour" checkbox ('' value). */
  auto?: string;
  /** Show this row only while the named boolean field is on. */
  needs?: keyof EventMarkerStyle;
  hint?: string;
}

export const MARKER_FIELDS: MarkerField[] = [
  { key: 'ring', label: 'Expanding ring', group: 'ring', kind: 'bool',
    hint: 'A ring that expands out of the marker while the event is new.' },
  { key: 'ringColor', label: 'Ring colour', group: 'ring', kind: 'color',
    auto: 'Follow the marker colour', needs: 'ring' },
  { key: 'ringMaxPx', label: 'Ring size (px)', group: 'ring', kind: 'num',
    min: 6, max: 90, step: 1, needs: 'ring' },
  { key: 'ringWidthPx', label: 'Ring thickness (px)', group: 'ring', kind: 'num',
    min: 0.5, max: 10, step: 0.5, needs: 'ring' },
  { key: 'ringPeriodMs', label: 'Ring speed (ms per expansion)', group: 'ring', kind: 'num',
    min: 200, max: 8000, step: 50, needs: 'ring' },
  { key: 'ringCount', label: 'Rings at once', group: 'ring', kind: 'num',
    min: 1, max: 4, step: 1, needs: 'ring' },
  { key: 'ringOpacity', label: 'Ring opacity', group: 'ring', kind: 'num',
    min: 0.05, max: 1, step: 0.05, needs: 'ring' },
  { key: 'ringSeconds', label: 'Ring runs for (s)', group: 'ring', kind: 'num',
    min: 0, max: 14400, step: 5, needs: 'ring',
    hint: '0 = the global flashing time above.' },

  { key: 'halo', label: 'Halo while new', group: 'body', kind: 'bool' },
  { key: 'haloColor', label: 'Halo colour', group: 'body', kind: 'color',
    auto: 'Follow the marker colour', needs: 'halo' },
  { key: 'haloPx', label: 'Halo size (px)', group: 'body', kind: 'num',
    min: 4, max: 60, step: 1, needs: 'halo' },
  { key: 'haloOpacity', label: 'Halo opacity', group: 'body', kind: 'num',
    min: 0.02, max: 1, step: 0.02, needs: 'halo' },
  { key: 'dot', label: 'Point marker', group: 'body', kind: 'bool',
    hint: 'The coloured dot. Turn it off for a symbol-only marker.' },
  { key: 'dotPx', label: 'Point size (px)', group: 'body', kind: 'num',
    min: 1, max: 24, step: 0.5, needs: 'dot' },
  { key: 'symbol', label: 'Kind symbol', group: 'body', kind: 'bool' },
  { key: 'symbolScale', label: 'Symbol size', group: 'body', kind: 'num',
    min: 0.3, max: 3, step: 0.05, needs: 'symbol' },
  { key: 'symbolTint', label: 'Tint the symbol', group: 'body', kind: 'bool',
    needs: 'symbol',
    hint: 'Draw the glyph as a solid silhouette in the marker colour, so it '
      + 'changes colour with age. Off keeps the natural colour emoji.' },
  { key: 'settleScale', label: 'Settles to', group: 'body', kind: 'num',
    min: 0.2, max: 2, step: 0.05,
    hint: 'Size once the event is no longer new — under 1 shrinks it away.' },

  { key: 'pulse', label: 'Size pulse', group: 'pulse', kind: 'bool',
    hint: 'The whole marker beats larger and smaller while the event is new.' },
  { key: 'pulsePct', label: 'Pulse intensity (%)', group: 'pulse', kind: 'num',
    min: 0, max: 150, step: 1, needs: 'pulse' },
  { key: 'pulsePeriodMs', label: 'Pulse speed (ms per beat)', group: 'pulse', kind: 'num',
    min: 200, max: 5000, step: 50, needs: 'pulse' },
  { key: 'pulseSeconds', label: 'Pulse runs for (s)', group: 'pulse', kind: 'num',
    min: 0, max: 14400, step: 5, needs: 'pulse',
    hint: '0 = the global flashing time above.' },
];

export const GROUP_LABELS: Record<FieldGroup, string> = {
  ring: 'Arrival ring',
  body: 'Marker body',
  pulse: 'Size pulse',
};

// --- normalisation ------------------------------------------------------------
// Everything arriving from the server (or a stale browser) goes through here.
// A blob from a future version with keys we don't know, or a number outside its
// bounds, must degrade to a sane marker rather than throw inside a render loop.

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

function clamp(value: unknown, lo: number, hi: number, fallback: number): number {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(hi, Math.max(lo, n));
}

function color(value: unknown, fallback: string, allowAuto: boolean): string {
  if (typeof value !== 'string') return fallback;
  const v = value.trim();
  if (!v) return allowAuto ? '' : fallback;
  return HEX_RE.test(v) ? v.toLowerCase() : fallback;
}

export const MAX_STAGES = 8;

function stage(raw: unknown, index: number): MarkerStage | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const c = color(r.color, '', false);
  if (!c) return null;
  return {
    atSeconds: clamp(r.atSeconds, 0, 604800, index === 0 ? 0 : 60),
    color: c,
    opacity: clamp(r.opacity, 0, 1, 1),
  };
}

/** Stages, sorted and capped. The first stop always covers from age 0 — a ramp
 *  whose earliest stop is at 5 min would otherwise leave the freshest events,
 *  the ones this whole feature is about, with no colour at all. */
export function normalizeStages(raw: unknown): MarkerStage[] {
  if (!Array.isArray(raw)) return [];
  const out: MarkerStage[] = [];
  for (const item of raw) {
    const s = stage(item, out.length);
    if (s) out.push(s);
    if (out.length >= MAX_STAGES) break;
  }
  out.sort((a, b) => a.atSeconds - b.atSeconds);
  if (out.length) out[0]!.atSeconds = 0;
  return out;
}

export function normalizeStyle(raw: unknown,
                               base: EventMarkerStyle = DEFAULT_MARKER_STYLE): EventMarkerStyle {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const out = { ...base, stages: base.stages.map((s) => ({ ...s })) };
  for (const f of MARKER_FIELDS) {
    if (!(f.key in r)) continue;
    const value = r[f.key];
    if (f.kind === 'bool') {
      (out as Record<string, unknown>)[f.key] = Boolean(value);
    } else if (f.kind === 'num') {
      (out as Record<string, unknown>)[f.key] =
        clamp(value, f.min ?? 0, f.max ?? 1e9, base[f.key] as number);
    } else {
      (out as Record<string, unknown>)[f.key] =
        color(value, base[f.key] as string, Boolean(f.auto));
    }
  }
  if ('stages' in r) out.stages = normalizeStages(r.stages);
  return out;
}

const KIND_RE = /^[a-z][a-z0-9_]{0,40}$/;

export function normalizeConfig(raw: unknown): EventMarkerConfig {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const base = normalizeStyle(r.default);
  const kinds: Record<string, EventMarkerStyle> = {};
  const rawKinds = (r.kinds && typeof r.kinds === 'object' ? r.kinds : {}) as
    Record<string, unknown>;
  for (const [key, value] of Object.entries(rawKinds)) {
    if (!KIND_RE.test(key)) continue;
    // A per-kind override is a FULL style: it falls back to the shipped
    // defaults for anything missing, never to the operator's edited default —
    // otherwise "customised" would quietly move when the default moved.
    kinds[key] = normalizeStyle(value, DEFAULT_MARKER_STYLE);
  }
  const glyphs: Record<string, string> = {};
  const rawGlyphs = (r.glyphs && typeof r.glyphs === 'object' ? r.glyphs : {}) as
    Record<string, unknown>;
  for (const [key, value] of Object.entries(rawGlyphs)) {
    if (!KIND_RE.test(key) || !(key in LIVE_KINDS)) continue;
    const g = normalizeGlyph(value);
    // A glyph equal to the shipped one is not an override. Storing it would
    // pin the kind to today's emoji through a later change of the default.
    if (g === null || g === SHIPPED_GLYPHS[key]) continue;
    glyphs[key] = g;
  }
  return { default: base, kinds, glyphs };
}

// --- the live config ----------------------------------------------------------

let config: EventMarkerConfig = DEFAULT_MARKER_CONFIG;

export function setEventMarkerConfig(raw: unknown): void {
  config = normalizeConfig(raw);
  applyGlyphOverrides(config.glyphs);
}

/** The glyph a kind renders with under `cfg`. The editor's preview asks with
 *  its UNSAVED config; everything else reads `LIVE_KINDS`, which
 *  `setEventMarkerConfig` has already rewritten to the same answer. */
export function glyphOf(kind: string, cfg: EventMarkerConfig = config): string {
  return cfg.glyphs[kind] ?? SHIPPED_GLYPHS[kind] ?? SHIPPED_GLYPHS.unclear!;
}

export function eventMarkerConfig(): EventMarkerConfig {
  return config;
}

/** The style that renders one kind. Callers pass a `knownKind()` value; an
 *  unknown one lands on the default, which is the correct answer for it. */
export function styleFor(kind: string): EventMarkerStyle {
  return config.kinds[kind] ?? config.default;
}

/** Every style the current config can produce, deduped — what the renderer
 *  walks to pre-build sprites, and what tells it whether ANY style needs the
 *  animation loop at all. */
export function allStyles(): EventMarkerStyle[] {
  return [config.default, ...Object.values(config.kinds)];
}

// --- the timing maths ---------------------------------------------------------
// These are pure so the map, the admin preview and the tests all read the same
// arithmetic. objects.ts mirrors ringProgress/pulseFactor as MapLibre
// expressions (it has to — they run per feature on the GPU clock); the mirror
// is called out at that site and pinned by eventstyle.test.ts.

/** How long the ring runs after the last report, ms. */
export function ringMs(s: EventMarkerStyle, freshMs: number): number {
  if (!s.ring) return 0;
  return s.ringSeconds > 0 ? s.ringSeconds * 1000 : freshMs;
}

/** How long the size pulse runs after the last report, ms. */
export function pulseMs(s: EventMarkerStyle, freshMs: number): number {
  if (!s.pulse || s.pulsePct <= 0) return 0;
  return s.pulseSeconds > 0 ? s.pulseSeconds * 1000 : freshMs;
}

/** How long the marker counts as NEW — the union of its announcements, which is
 *  when `settleScale` takes over. */
export function newMs(s: EventMarkerStyle, freshMs: number): number {
  return Math.max(ringMs(s, freshMs), pulseMs(s, freshMs));
}

/** Ring `i` of `ringCount`, at animation time `tMs`: 0 at the marker, 1 fully
 *  expanded and gone. Rings are spread evenly through the cycle. */
export function ringProgress(s: EventMarkerStyle, i: number, tMs: number): number {
  const x = tMs / Math.max(1, s.ringPeriodMs) + i / Math.max(1, s.ringCount);
  return x - Math.floor(x);
}

/** The size multiplier of the beat at animation time `tMs`: 1 at the trough,
 *  1 + pulsePct/100 at the peak. */
export function pulseFactor(s: EventMarkerStyle, tMs: number): number {
  const phase = (2 * Math.PI * tMs) / Math.max(1, s.pulsePeriodMs);
  return 1 + (s.pulsePct / 100) * (1 + Math.sin(phase)) / 2;
}

/** The stage covering this occurrence age, or null when the style has no ramp
 *  of its own (the common case — the global age ramp answers instead). */
export function stageAt(s: EventMarkerStyle, ageMs: number): MarkerStage | null {
  if (!s.stages.length) return null;
  const seconds = Math.max(0, ageMs) / 1000;
  let found = s.stages[0]!;
  for (const st of s.stages) {
    if (st.atSeconds <= seconds) found = st;
    else break;
  }
  return found;
}

/** Milliseconds until this marker's appearance next changes by itself — the
 *  ring stopping, the pulse stopping, the next colour stage starting.
 *
 *  The renderer schedules its next re-bake off the soonest of these across all
 *  events rather than polling on a fixed timer: a 15 s poll makes a 5 s stage
 *  invisible, and a 1 s poll re-serialises every event on the map once a second
 *  forever. Infinity means nothing further will change on its own. */
export function nextBoundaryMs(s: EventMarkerStyle, reportAgeMs: number,
                               occurrenceAgeMs: number, freshMs: number): number {
  let best = Infinity;
  const consider = (deadline: number, age: number) => {
    const delta = deadline - age;
    if (delta > 0 && delta < best) best = delta;
  };
  consider(ringMs(s, freshMs), reportAgeMs);
  consider(pulseMs(s, freshMs), reportAgeMs);
  for (const st of s.stages) consider(st.atSeconds * 1000, occurrenceAgeMs);
  return best;
}
